import { EE_STATUS, LOCAL_STORAGE_KEY } from '@common/constants';
import { CustomerInfo, DEFAULT_ESTIMATE, Estimate } from '@common/models';
import { EstimateStatus } from '@common/models/save-estimate-dto';
import { LoadEstimateKeys } from '@core/hooks/useEstimate';
import { removeStorageData } from '@core/services/local-storage-info';
import type { RootState } from '@redux/store';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

interface EstimateState {
    /**
     * the most recently generated estimate from the design
     */
    currentEstimate: Estimate;
    /**
     * the customer name/address/phone data entered while saving
     */
    customerData?: CustomerInfo;
    /**
     * is the customerData copied from a fetchedEstimate?
     */
    isFetchedCustomerData: boolean;
    loadEstimateKeys?: LoadEstimateKeys;
    /**
     * this is related to, but not the same as isSaveLoading
     */
    isSavingEstimate: boolean;
    eeStatusList: EE_STATUS[];
    /**
     * user entered ticket number, used by ticketValidationService as parameter to the API
     */
    validSmallTicketNumber?: string;
    discountOverride?: number;
    ignoreFetchedEstimate: boolean;
    prevEstimateStatus: EstimateStatus;
}

export const nullEstimateStatus = {
    imeMessage: null,
    imeStatus: null,
    paymentUrl: null,
    status: null,
};

const EMPTY_CUSTOMER_INFO: CustomerInfo = {
    customerName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    alternatePhone: '',
    sendCustomerEmail: true,
};

// initial customer data from local storage if available
const initialCustomerData = (): CustomerInfo => {
    // TODO Remove this after a few months in use 2025-10-01 is fine. This just cleans up old stored data we aren't using any more
    removeStorageData(LOCAL_STORAGE_KEY.SAVED_CUSTOMER_DATA);
    removeStorageData(LOCAL_STORAGE_KEY.REMEMBER_ME);

    return EMPTY_CUSTOMER_INFO;
};

const initialState: EstimateState = {
    currentEstimate: DEFAULT_ESTIMATE,
    customerData: initialCustomerData(),
    isFetchedCustomerData: false,
    eeStatusList: [],
    loadEstimateKeys: undefined,
    isSavingEstimate: false,
    validSmallTicketNumber: undefined,
    discountOverride: undefined,
    ignoreFetchedEstimate: false,
    prevEstimateStatus: new EstimateStatus(),
};

const estimateSlice = createSlice({
    name: 'estimate',
    initialState,
    reducers: {
        setDiscountOverride: (state, action: PayloadAction<number | undefined>) => {
            state.discountOverride = action.payload;
        },
        resetDiscountOverride: (state) => {
            state.discountOverride = initialState.discountOverride;
        },
        updateCurrentEstimate: (state, action: PayloadAction<Estimate>) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            state.currentEstimate = action.payload;
        },
        updateCustomerData: (state, action: PayloadAction<CustomerInfo | undefined>) => {
            state.customerData = action.payload ?? EMPTY_CUSTOMER_INFO;
        },
        resetCustomerData: (state) => {
            state.customerData = initialCustomerData();
        },
        updateIsFetchedCustomerData: (state, action: PayloadAction<boolean>) => {
            state.isFetchedCustomerData = action.payload;
        },
        updateLoadEstimateKeys: (state, action: PayloadAction<LoadEstimateKeys | undefined>) => {
            state.loadEstimateKeys = action.payload;
        },
        updateIsSavingEstimate: (state, action: PayloadAction<boolean>) => {
            state.isSavingEstimate = action.payload;
        },
        updateEeStatusList: (state, action: PayloadAction<EE_STATUS[]>) => {
            state.eeStatusList = action.payload;
        },
        updateValidSmallTicketNumber: (state, action: PayloadAction<string | undefined>) => {
            state.validSmallTicketNumber = action.payload;
        },
        updateIgnoreFetchedEstimate: (state, action: PayloadAction<boolean>) => {
            state.ignoreFetchedEstimate = action.payload;
        },
        updatePrevEstimateStatus: (state, action: PayloadAction<EstimateStatus>) => {
            state.prevEstimateStatus = action.payload;
        },
    },
});

export const selectCurrentEstimate = ({ estimate }: RootState): Estimate => estimate.currentEstimate;
export const selectCustomerData = ({ estimate }: RootState): CustomerInfo =>
    estimate.customerData ?? EMPTY_CUSTOMER_INFO;
export const selectIsFetchedCustomerData = ({ estimate }: RootState): boolean => estimate.isFetchedCustomerData;
export const selectLoadEstimateKeys = ({ estimate }: RootState): LoadEstimateKeys | undefined =>
    estimate.loadEstimateKeys;
export const selectIsSavingEstimate = ({ estimate }: RootState): boolean => estimate.isSavingEstimate;
export const selecteEeStatusList = ({ estimate }: RootState): EE_STATUS[] => estimate.eeStatusList;
export const selectValidSmallTicketNumber = ({ estimate }: RootState): string | undefined =>
    estimate.validSmallTicketNumber;
export const selectIgnoreFetchedEstimate = ({ estimate }: RootState): boolean => estimate.ignoreFetchedEstimate;

export const selectDiscountOverride = ({ estimate }: RootState): number | undefined => estimate.discountOverride;
export const selectPrevEstimateStatus = ({ estimate }: RootState): EstimateStatus => estimate.prevEstimateStatus;

export default estimateSlice.reducer;
export const {
    updateCurrentEstimate,
    updateCustomerData,
    updateIsFetchedCustomerData,
    updateLoadEstimateKeys,
    updateIsSavingEstimate,
    updateEeStatusList,
    updateValidSmallTicketNumber,
    setDiscountOverride,
    resetDiscountOverride,
    updateIgnoreFetchedEstimate,
    resetCustomerData,
    updatePrevEstimateStatus,
} = estimateSlice.actions;
